import { apiClient } from '@/lib/api-client';
import { HTTPError } from 'ky';

/**
 * 거래처 발행설정 편집에 쓰는 코드값 정의와 저장 호출.
 *
 * 발행기간유형 라벨은 `issue-period.ts`의 정본과 같아야 한다 — 이 값이 C410 대상 거래처 조회의
 * 기준이라, 화면 라벨이 정본과 어긋나면 사용자가 잘못 고르고 대상이 비게 된다.
 */

/**
 * 편집 가능한 필드. 서버 PATCH 요청 키와 같다.
 *
 * 기본정보(회사·매장·거래처번호·거래처명·거래처종류)를 뺀 V47/V51 신규 컬럼 전체다.
 */
export const INVOICE_SETTING_FIELDS = [
  'invoiceIssueTerm',
  'receiptIssueType',
  'useGeoEcode',
  'statementPrintType',
  'invoicePrintType1',
  'invoicePrintType2',
  'supplyAmtCalcType',
  'transMethod',
  'gen2StoreYn',
  'cntrMgmtType',
  'posQtyRoundType',
  'amtRoundType',
  'deptCd',
  'email',
  'emailRecipient',
] as const;

export type InvoiceSettingField = (typeof INVOICE_SETTING_FIELDS)[number];

export function isInvoiceSettingField(name: string): name is InvoiceSettingField {
  return (INVOICE_SETTING_FIELDS as readonly string[]).includes(name);
}

/** 0(미발행)도 편집에서는 고를 수 있어야 한다 — 발행 대상에서 빼는 것이 정상 운영이다. */
export const ISSUE_TERM_CODES = ['0', '1', '2', '3', '4', '5', '6'];
export const ISSUE_TERM_LABELS = [
  '0. 미발행',
  '1. 말일',
  '2. 15일',
  '3. 20일',
  '4. 25일',
  '5. 말일 하루 전',
  '6. 수시',
];

/** 정본 pos_테이블스펙명세서.xlsx dealer.receipt_issue_type — 9종 (기본 4.등록기) */
export const RECEIPT_ISSUE_CODES = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
export const RECEIPT_ISSUE_LABELS = [
  '1. 세금계산서',
  '2. 면세(영세율)',
  '3. 주민등록 기재분',
  '4. 등록기',
  '5. 신용카드사용분',
  '6. 상품권사용분',
  '7. 이전거래',
  '8. 이관출',
  '9. 기타',
];

/** 서버(C410 scope)와 정본 프로토타입이 '1'만 사용으로 본다 — Y/N이 아니다 */
export const USE_GEO_ECODE_CODES = ['1', '2'];
export const USE_GEO_ECODE_LABELS = ['1. 사용', '2. 사용안함'];

export const PRINT_TYPE_CODES = ['1', '2', '3', '4', '5', '6', '7'];
export const PRINT_TYPE_LABELS = [
  '1. 유종별,차량별,단가별집계',
  '2. 유종별,단가별집계',
  '3. 유종별,일자별,단가별집계',
  '4. 유종(1장씩),차량별,단가별집계',
  '5. 유종별,차량별,일자별리스트',
  '6. 유종,차량별집계(평균단가)',
  '7. 유종(1장씩),차량,일자별리스트',
];

export const INVOICE_PRINT_TYPE_CODES = ['1', '2'];
export const INVOICE_PRINT_TYPE_LABELS = ['1. 단가표시', '2. 단가미표시'];

/** 계산서인쇄유형2 — 인쇄유형1(단가표시 여부)과 다른 6종 코드다 */
export const INVOICE_PRINT_TYPE2_CODES = ['1', '2', '3', '4', '5', '6'];
export const INVOICE_PRINT_TYPE2_LABELS = [
  '1. 유종전체',
  '2. 공제/미공제',
  '3. 유종1건당',
  '4. 기타발행',
  '5. 유종전체(경유외)',
  '6. 기타상품(특정상품외)',
];

export const TRANS_METHOD_CODES = ['1', '2', '3', '4'];
export const TRANS_METHOD_LABELS = [
  '1. 자차',
  '2. 용차',
  '3. 정유사직배',
  '4. 거래처자차',
];

export const GEN2_STORE_CODES = ['1', '2'];
export const GEN2_STORE_LABELS = ['1. 허용', '2. 미허용'];

export const CNTR_MGMT_CODES = ['1', '2', '3'];
export const CNTR_MGMT_LABELS = ['1. 당사', '2. 위탁', '3. 해당없음'];

export const POS_QTY_ROUND_CODES = ['0', '1', '2', '3'];
export const POS_QTY_ROUND_LABELS = [
  '0. 미적용',
  '1. 올림',
  '2. 반올림',
  '3. 버림',
];

/** 절사(0~5)와 올림(A~E)이 한 코드 공간에 있다 */
export const AMT_ROUND_CODES = [
  '0', '1', '2', '3', '4', '5', 'A', 'B', 'C', 'D', 'E',
];
export const AMT_ROUND_LABELS = [
  '0. 해당없음',
  '1. 원단위절사',
  '2. 십단위절사',
  '3. 백단위절사',
  '4. 천단위절사',
  '5. 만단위절사',
  'A. 원단위올림',
  'B. 십단위올림',
  'C. 백단위올림',
  'D. 천단위올림',
  'E. 만단위올림',
];

export const SUPPLY_AMT_CALC_CODES = ['1', '2', '3'];
export const SUPPLY_AMT_CALC_LABELS = [
  '1. 건별 산출',
  '2. 차량별 집계산출',
  '3. 유종별 집계 산출',
];

/**
 * 셀 하나를 저장한다. 보낸 필드만 반영되고 나머지 컬럼은 서버에서 건드리지 않는다.
 *
 * 실패하면 서버가 준 문구를 그대로 담아 던진다 - 코드 공간이 어긋나 거절당하는 실패는
 * 이 문구로만 드러난다(useGeoEcode 가 서버에서 Y/N 이었던 사례).
 */
export async function saveInvoiceSetting(
  storeCd: string,
  dealCode: string,
  field: InvoiceSettingField,
  value: string | null
) {
  try {
    await apiClient.patch(
      `master/cust/${encodeURIComponent(storeCd)}/${encodeURIComponent(dealCode)}/invoice-settings`,
      { json: { [field]: value } }
    );
  } catch (error) {
    throw new Error(await serverMessage(error));
  }
}

async function serverMessage(error: unknown): Promise<string> {
  if (!(error instanceof HTTPError)) {
    return '값을 저장하지 못했습니다';
  }

  const body = (await error.response.json().catch(() => null)) as {
    message?: string;
    errors?: { reason?: string }[];
  } | null;

  return (
    [body?.message, body?.errors?.[0]?.reason].filter(Boolean).join(' - ') ||
    `저장하지 못했습니다 (HTTP ${error.response.status})`
  );
}
